import React from "react";
import { brl, pct } from "../utils/formatters";

const ALPHAS = [1, 0.86, 0.74, 0.63, 0.53, 0.45];

/**
 * Faixa de destaque da apuração — equivalente ao `.hero` do protótipo:
 * valor a recolher em destaque, faturamento líquido, líquido após tributos
 * e a divisão do total entre os tributos (stack bar + lista).
 * `m` = período (semana ou mês), `isMes` alterna para a visão consolidada.
 */
export default function HeroPanel({ m, isMes, nomeSemana }) {
  const total = isMes ? m.total : m.totalC;
  const pay = isMes ? m.pay : m.payC;
  const credor = total < 0;

  const label = isMes
    ? "Tributos a recolher no mês"
    : credor
      ? `Saldo credor da ${(nomeSemana || "").toLowerCase()}`
      : `Tributos a recolher · ${nomeSemana}`;

  let texto;
  if (isMes) {
    texto =
      `${pct(total / m.liq)} do faturamento líquido e ${pct(total / m.bruto)} do bruto. ` +
      `O ICMS responde por ${pct(m.pay[0][1] / total, 0)} do total, e o ICMS e o COFINS juntos por ${pct((m.pay[0][1] + m.pay[1][1]) / total, 0)}.`;
  } else if (total < 0.005) {
    texto =
      "Nada a recolher nesta semana: os créditos, somados ao saldo da semana anterior, cobrem os débitos. " +
      (m.carryOut > 0.005 ? `Saldo credor de ${brl(m.carryOut)} segue para a semana seguinte.` : "");
  } else {
    texto =
      "Débito menos crédito de cada tributo" +
      (m.carryIn > 0.005 ? `, já descontado o saldo credor de ${brl(m.carryIn)} da semana anterior` : "") +
      ", mais ICMS-ST e DIFAL." +
      (m.carryOut > 0.005 ? ` Saldo credor de ${brl(m.carryOut)} segue para a semana seguinte.` : "");
  }

  const e = m.eff.total;
  const nota =
    Math.abs(e) < 0.005
      ? null
      : `Os lançamentos manuais ${e < 0 ? "reduzem" : "aumentam"} o total em ${brl(Math.abs(e))}.`;

  const liq = m.liq;
  const apos = liq - total;
  const pos = pay.filter((p) => p[1] > 0.005);
  const posSum = pos.reduce((s, p) => s + p[1], 0);

  const ordenado = pay.slice().sort((a, b) => b[1] - a[1]);
  let ai = 0;

  return (
    <section className="hero-panel">
      <div className="hero-main">
        <span className="hero-label">{label}</span>
        <span className="hero-value">{brl(Math.abs(total))}</span>
        <p className="hero-text">{texto}</p>
        {nota && <p className="hero-note">{nota}</p>}
      </div>

      <div className="hero-side">
        <div className="hs">
          <span className="l">Faturamento líquido</span>
          <span className="v">{brl(liq)}</span>
          <span className="n">
            {isMes
              ? `Bruto de ${brl(m.bruto)} menos devoluções e ajuste`
              : `Vendas de ${brl(m.bruto)} menos devoluções de ${brl(m.dev)}`}
          </span>
        </div>
        <div className="hs">
          <span className="l">Líquido após tributos</span>
          <span className="v">{brl(apos)}</span>
          <span className="n">
            {isMes
              ? `${pct(apos / m.bruto)} do faturamento bruto`
              : m.liq < 0
                ? "Devoluções de vendas de outras semanas superam as vendas desta"
                : "Faturamento líquido menos tributos da semana"}
          </span>
        </div>
      </div>

      <div className="hero-split">
        <span className="split-title">Quanto recolher, por tributo</span>
        {pos.length > 0 && (
          <div className="split-stack">
            {ordenado
              .filter((r) => r[1] > 0.005)
              .map((r, i) => (
                <div
                  key={r[0]}
                  className="seg"
                  style={{
                    width: `${((r[1] / posSum) * 100).toFixed(3)}%`,
                    background: `rgba(16,185,129,${ALPHAS[Math.min(i, 5)]})`,
                  }}
                  title={`${r[0]} · ${brl(r[1])} · ${pct(r[1] / posSum)} do valor a recolher`}
                />
              ))}
          </div>
        )}
        <ul className="split-list">
          {pos.length === 0 && (
            <li className="empty" style={{ gridColumn: "1 / -1" }}>
              Nenhum tributo com valor a recolher nesta semana.
            </li>
          )}
          {ordenado.map((r) => {
            const v = r[1];
            if (v > 0.005) {
              const col = `rgba(16,185,129,${ALPHAS[Math.min(ai, 5)]})`;
              ai += 1;
              return (
                <li key={r[0]}>
                  <span className="nm"><i style={{ background: col }} />{r[0]}</span>
                  <span className="vl">{brl(v)}</span>
                  <span className="pc">{pct(v / posSum)}</span>
                </li>
              );
            }
            return (
              <li key={r[0]}>
                <span className="nm"><i className="hollow" />{r[0]}</span>
                <span className="vl neg">{brl(v < -0.005 ? v : 0)}</span>
                <span className="pc">
                  {v < -0.005 ? "saldo credor" : r[0] === "ISS" && !isMes ? "entra no mês" : "sem valor"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
